import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { User, Wallet, TrendingUp, Image, Flame } from "lucide-react";
import { UserAnalytics, NFTAnalytics, UserDailyStats } from "@/hooks/useAnalytics";
import { formatDate, shortenWallet } from "@/lib/exportUtils";
import { priceToUsd, formatUsdPrice } from "@/lib/priceUtils";
import { useSolPrice } from "@/hooks/useSolPrice";
import { StatCard } from "./StatCard";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

interface MyAnalyticsSummaryProps {
  user: UserAnalytics | null;
  nfts: NFTAnalytics[];
  dailyStats?: UserDailyStats[];
}

export function MyAnalyticsSummary({ user, nfts, dailyStats = [] }: MyAnalyticsSummaryProps) {
  const { usdToSol } = useSolPrice();

  // Format price: USD primary, SOL secondary
  const formatPriceDisplay = (value: number) => {
    const usdValue = priceToUsd(value);
    const solValue = usdToSol(usdValue);
    return `${formatUsdPrice(usdValue)} ≈ ${solValue.toFixed(4)} SOL`;
  };

  // Format price with separate values
  const formatPriceSplit = (value: number) => {
    const usdValue = priceToUsd(value);
    const solValue = usdToSol(usdValue);
    return {
      primary: formatUsdPrice(usdValue),
      secondary: `≈ ${solValue.toFixed(4)} SOL`
    };
  };

  const t = {
    connectWallet: "Connect your wallet to view your analytics",
    joined: "Joined",
    nftsOwned: "NFTs Owned",
    totalSales: "Total Sales",
    totalEarned: "Total Earned",
    fromNftSales: "From NFT sales",
    activityOverTime: "NFT Activity",
    sales: "Sales",
    purchases: "Purchases",
    noData: "No data available",
    myTrendingNfts: "My Trending NFTs",
    noNfts: "No NFTs yet",
    salesCount: "sales",
  };

  // Sort NFTs by performance (total_earned first, then sales_count)
  const trendingNfts = [...nfts]
    .sort((a, b) => {
      if (b.total_earned !== a.total_earned) return b.total_earned - a.total_earned;
      return b.sales_count - a.sales_count;
    })
    .slice(0, 5);

  if (!user) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          {t.connectWallet}
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data - last 7 days for mobile-friendly display
  const chartData = dailyStats.slice(-7).map(d => ({
    date: new Date(d.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    sales: d.sales,
    purchases: d.purchases,
    earned: d.earned,
  }));

  const hasChartData = chartData.some(d => d.sales > 0 || d.purchases > 0);

  return (
    <div className="space-y-6">
      {/* User Profile Summary */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage src={user.avatar_url || undefined} />
              <AvatarFallback>
                <User className="w-8 h-8" />
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="text-xl font-bold">
                {user.display_name || shortenWallet(user.wallet_address)}
              </h3>
              <p className="text-sm text-muted-foreground font-mono">
                {shortenWallet(user.wallet_address, 8)}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline">{user.role}</Badge>
                <span className="text-xs text-muted-foreground">
                  {t.joined} {formatDate(user.joined_at)}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid - Separate cards for Sales and Purchases */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title={t.nftsOwned}
          value={user.nfts_owned}
          icon={Image}
          iconColor="text-primary"
        />
        <StatCard
          title="Sales"
          value={user.total_nft_sales}
          icon={TrendingUp}
          iconColor="text-green-500"
        />
        <StatCard
          title="Purchases"
          value={(user as any).total_purchases ?? 0}
          icon={TrendingUp}
          iconColor="text-cyan-500"
        />
        <StatCard
          title={t.totalEarned}
          value={formatPriceDisplay(user.total_nft_earned)}
          subtitle={t.fromNftSales}
          icon={Wallet}
          iconColor="text-yellow-500"
        />
      </div>

      {/* Personal Activity Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            {t.activityOverTime}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasChartData ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" vertical={false} />
                <XAxis
                  dataKey="date"
                  className="text-xs"
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  className="text-xs"
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                />
                <Bar 
                  dataKey="purchases" 
                  fill="hsl(186 100% 50%)" 
                  name={t.purchases} 
                  radius={[4, 4, 0, 0]}
                  maxBarSize={30}
                />
                <Bar 
                  dataKey="sales" 
                  fill="hsl(142 76% 45%)" 
                  name={t.sales} 
                  radius={[4, 4, 0, 0]}
                  maxBarSize={30}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
              {t.noData}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Trending NFTs - Visual List */}
      <Card className="bg-gradient-to-br from-primary/5 to-transparent border-primary/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary to-accent">
              <Flame className="w-4 h-4 text-white" />
            </div>
            <span className="text-primary">{t.myTrendingNfts}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trendingNfts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t.noNfts}
            </div>
          ) : (
            <div className="space-y-3">
              {trendingNfts.map((nft, index) => {
                // Keep price display consistent with Marketplace cards:
                // show current bonding-curve buy price stored in DB (current_price || price)
                const currentPriceUsd = priceToUsd(nft.current_price || nft.price);
                const solValue = usdToSol(currentPriceUsd);
                const priceDisplay = {
                  primary: formatUsdPrice(currentPriceUsd),
                  secondary: `≈ ${solValue.toFixed(4)} SOL`
                };
                return (
                  <div
                    key={nft.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full font-bold text-sm bg-gradient-to-br text-white",
                      "from-primary to-accent"
                    )}>
                      {index + 1}
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                      {nft.image_url ? (
                        <img
                          src={nft.image_url}
                          alt={nft.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{nft.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {user?.display_name || shortenWallet(user?.wallet_address || "")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm text-green-500">
                        {priceDisplay.primary}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {priceDisplay.secondary}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {nft.sales_count} {t.salesCount}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
